import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { portalConversationsApi, portalExportApi, portalDocumentsApi, portalKnowledgeApi, portalAssetsApi } from '../../services/api';
import { useChatStore, usePortalStore, toast } from '../../stores';
import { useStreamChat } from '../../hooks/useStreamChat';
import { NexusChatBubble, NexusTypingIndicator } from '../../components/nexus-ui/NexusChat';
import { InsightRevealToast } from '../../components/instrument/InsightRevealToast';
import { AgentOrchestrationGraph } from '../../components/instrument/AgentOrchestrationGraph';
import { DocGenProgressCard } from '../../components/instrument/DocGenProgressCard';
import { DocGenActivityDrawer } from '../../components/instrument/DocGenActivityDrawer';
import { HtmlArtifactPanel } from '../../components/instrument/HtmlArtifactPanel';
import { extractHtmlArtifact } from '../../components/instrument/htmlArtifact';
import { InfographicRenderer } from '../../components/instrument/InfographicRenderer';
import ApsViewerPanel from '../../components/instrument/ApsViewerPanel';
import { MppConversionHelpModal } from '../../components/instrument/MppConversionHelpModal';
import {
  Send, Plus, MessageSquare, Search, Sparkles,
  PanelLeftClose, PanelLeftOpen, Download,
  Paperclip, FileText, Table, Presentation,
  GitBranch, Network, ArrowRightLeft, BarChart3,
  X, ChevronDown, FileDown, Loader2, Cpu, Brain, Pin, Trash2, MoreVertical, Pencil, CircleSlash, ClipboardList, Database, Check, Eye, Globe, Cuboid,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// 3D model extensions — when dropped into the chat composer they route
// through APS upload + viewer rather than the standard text-extraction path.
const ASSET_3D_EXT = new Set([
  '.rvt', '.rfa', '.rte', '.ifc', '.dwg', '.dxf', '.dwf', '.dwfx',
  '.nwd', '.nwc', '.nwf', '.3dm', '.obj', '.fbx', '.gltf', '.glb',
  '.stp', '.step', '.iges', '.igs', '.skp', '.f3d', '.iam', '.ipt',
  '.sldprt', '.sldasm', '.max', '.prt',
]);
const is3DAsset = (filename = '') => {
  const lower = filename.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 && ASSET_3D_EXT.has(lower.slice(dot));
};

const EXPORT_FORMATS = [
  { id: 'json', label: 'JSON', ext: 'json', mime: 'application/json' },
  { id: 'pdf', label: 'PDF', ext: 'pdf', mime: 'application/pdf' },
  { id: 'csv', label: 'CSV', ext: 'csv', mime: 'text/csv' },
];

const ExportDropdown = ({ conversationId, title }) => {
  const [open, setOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const res = await portalExportApi.exportConversation(conversationId, format);
      const fmt = EXPORT_FORMATS.find(f => f.id === format);
      const blob = new Blob([res.data], { type: fmt?.mime || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'conversation'}.${fmt?.ext || format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silent fail
    } finally {
      setExporting(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(p => !p)}
        className="p-1.5 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors"
        data-testid="export-conv-btn"
        title="Export conversation"
      >
        {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -5, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -5, scale: 0.95 }}
            className="absolute right-0 top-full mt-1 w-36 bg-nexus-bg-secondary border border-nexus-border-emphasized rounded-lg shadow-xl z-50 overflow-hidden"
          >
            {EXPORT_FORMATS.map(fmt => (
              <button
                key={fmt.id}
                onClick={() => handleExport(fmt.id)}
                className="w-full px-3 py-2 text-left text-xs text-nexus-text-primary hover:bg-nexus-bg-tertiary/50 transition-colors flex items-center gap-2"
                data-testid={`export-${fmt.id}`}
              >
                <Download size={12} />
                Export as {fmt.label}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const DOC_FORMATS = [
  { id: 'docx', label: 'Word Document', icon: FileText, color: '#2B7CD3' },
  { id: 'xlsx', label: 'Spreadsheet', icon: Table, color: '#1D7044' },
  { id: 'pptx', label: 'Presentation', icon: Presentation, color: '#D04423' },
  { id: 'flowchart', label: 'Flowchart', icon: GitBranch, color: '#F26B1F' },
  { id: 'mindmap', label: 'Mind Map', icon: Network, color: '#1E4E8C' },
  { id: 'sequence', label: 'Sequence Diagram', icon: ArrowRightLeft, color: '#92580A' },
  { id: 'infographic', label: 'Infographic', icon: BarChart3, color: '#7A1F1F' },
];

// Pulls a scale hint out of free-form text:
//   "10,000 rows" / "50k items" / "100 page report" / "60 slide deck"
// Returns null if no hint is found, or a positive int (capped at 100k).
function parseScaleFromPrompt(text, fmt) {
  if (!text || typeof text !== 'string') return null;
  const t = text.toLowerCase();
  // Vocabulary covers singular AND plural — users say "5000 activity" or
  // "5000 activities" interchangeably. Same for tasks, milestones, etc.
  const patterns = {
    xlsx: [
      /(\d{1,3}(?:[,_]\d{3})+|\d+)\s*k\b\s*(?:rows?|items?|records?|entr(?:y|ies)|activit(?:y|ies)|tasks?|deliverables?|milestones?|line\s*items?)/i,
      /(\d{1,3}(?:[,_]\d{3})+|\d+)\s*(?:rows?|items?|records?|entr(?:y|ies)|activit(?:y|ies)|tasks?|deliverables?|milestones?|line\s*items?)/i,
    ],
    docx: [/(\d{1,4})\s*-?\s*(?:pages?|page-long|paged)\b/i],
    pptx: [/(\d{1,4})\s*-?\s*(?:slides?|slide-long|slide\s*deck)\b/i],
  }[fmt] || [];
  for (const rx of patterns) {
    const m = t.match(rx);
    if (m) {
      const raw = m[1].replace(/[,_]/g, '');
      let n = parseInt(raw, 10);
      if (Number.isFinite(n)) {
        if (rx.source.includes('\\s*k\\b')) n *= 1000;
        return Math.max(1, Math.min(100_000, n));
      }
    }
  }
  return null;
}

const PortalChat = () => {
  'use no memo'; // React Compiler — opt out for this large stateful component
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [input, setInput] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedText, setUploadedText] = useState('');
  // 3D asset uploaded into the chat composer — when set, an inline
  // viewer drawer is available and the chat injects model metadata as
  // context for assistant replies.
  const [inlineAsset, setInlineAsset] = useState(null);
  const [showAssetPanel, setShowAssetPanel] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const [targetScale, setTargetScale] = useState(''); // numeric override for rows / pages / slides
  const [selectedModel, setSelectedModel] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [showModelMenu, setShowModelMenu] = useState(false);
  const [memoryDrawerOpen, setMemoryDrawerOpen] = useState(false);
  const [activityDrawerOpen, setActivityDrawerOpen] = useState(false);
  // HTML artifact preview panel
  const [artifactOpen, setArtifactOpen] = useState(false);
  const [activeArtifact, setActiveArtifact] = useState(null);
  // Live ticker — capture when streaming started so the StreamTicker
  // can show elapsed seconds & tok/s. Note: the useEffect that reads
  // `isStreaming` must run AFTER `isStreaming` is destructured below;
  // we only declare the state slot here and bind the effect later.
  const [streamStartedAt, setStreamStartedAt] = useState(null);
  // KB scoping — user-selected KB ids (multi-select). Empty = all KBs.
  const [selectedKbIds, setSelectedKbIds] = useState([]);
  const [kbPickerOpen, setKbPickerOpen] = useState(false);
  // Reply language preference — 'en' | 'ja' | 'ar'. Persisted in
  // localStorage so the user's choice survives page reloads.
  const [language, setLanguage] = useState(() => {
    try { return localStorage.getItem('cognara.chat.lang') || 'en'; } catch { return 'en'; }
  });
  useEffect(() => { try { localStorage.setItem('cognara.chat.lang', language); } catch {} }, [language]);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [insight, setInsight] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  // Pending binary-MPP file shown in the conversion-help modal. When
  // non-null, we render <MppConversionHelpModal> with this file.
  const [pendingMppFile, setPendingMppFile] = useState(null);

  const {
    messages, setMessages, addMessage, activeConversationId,
    setActiveConversation, clearMessages,
    docGenProgress, setDocGenProgress,
  } = useChatStore();

  const {
    streamMessage, isStreaming, streamingContent, sources, toolCalls, activeTools, memory, resetStream, abort: stopStream,
  } = useStreamChat();

  // Stream-start timestamp lifecycle — bound after `isStreaming` is in scope.
  useEffect(() => {
    if (isStreaming && !streamStartedAt) setStreamStartedAt(Date.now());
    if (!isStreaming && streamStartedAt) setStreamStartedAt(null);
  }, [isStreaming, streamStartedAt]);

  // Click-outside to close any composer popover (Format / KB / Model / Lang).
  // Each popover lives inside `composerToolbarRef`; a click landing
  // anywhere else dismisses all four so the user doesn't have to
  // toggle them off manually.
  const composerToolbarRef = useRef(null);
  useEffect(() => {
    if (!showFormatMenu && !kbPickerOpen && !showModelMenu && !langPickerOpen) return;
    const onClick = (e) => {
      if (composerToolbarRef.current && !composerToolbarRef.current.contains(e.target)) {
        setShowFormatMenu(false);
        setKbPickerOpen(false);
        setShowModelMenu(false);
        setLangPickerOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [showFormatMenu, kbPickerOpen, showModelMenu, langPickerOpen]);

  // Per-conversation gate — the docgen progress card is "live" in the
  // conversation that started it. Any OTHER conversation should be fully
  // usable (input enabled, no overlay), even though the docgen job is still
  // running in the background. Without this scoping the same progress card
  // leaks into every chat the user opens.
  const docGenInThisConvo = !!(isGeneratingDoc && docGenProgress && (
    docGenProgress.conversationId === conversationId ||
    (!conversationId && (docGenProgress.conversationId || '').startsWith('new-'))
  ));

  // Load conversations
  const { data: conversations = [], refetch: refetchConversations } = useQuery({
    queryKey: ['conversations'],
    queryFn: () => portalConversationsApi.list({ limit: 30 }).then(r => r.data),
    staleTime: 30000,
  });

  // Load available models
  useEffect(() => {
    fetch(`${API_BASE}/api/models/available`)
      .then(r => r.ok ? r.json() : [])
      .then(models => {
        setAvailableModels(models);
        if (!selectedModel && models.length) {
          // Prefer the model marked default:true (Sonnet 4.6 — faster, cheaper);
          // fall back to first in list.
          const def = models.find((m) => m.default) || models[0];
          setSelectedModel(def.id);
        }
      })
      .catch(() => {});
  }, []);

  // Load specific conversation
  const { data: conversationData } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => portalConversationsApi.get(conversationId).then(r => r.data),
    enabled: !!conversationId,
  });

  // Load knowledge bases — fuels the composer KB picker that lets the
  // user scope the assistant's retrieval to one or more KBs.
  const { data: kbList = [] } = useQuery({
    queryKey: ['portalKnowledge'],
    queryFn: () => portalKnowledgeApi.list().then(r => r.data),
    staleTime: 60_000,
  });

  // Load conversation messages
  useEffect(() => {
    if (conversationData?.messages) {
      setMessages(conversationData.messages.map(m => ({
        role: m.role,
        // Re-append the "stopped by you" marker for partial assistant
        // messages so the visual cue survives a page reload.
        content: (m.partial && m.role === 'assistant' && !/—\s*stopped by you/i.test(m.content || ''))
          ? `${m.content || ''}\n\n_— stopped by you_`
          : m.content,
        sources: m.sources,
        timestamp: m.timestamp,
        partial: m.partial,
        kbScope: m.kb_scope || null,
        meter: m.meter || null,
      })));
      setActiveConversation(conversationId);
    }
  }, [conversationData, conversationId, setMessages, setActiveConversation]);

  // Auto-scroll only when the user is already near the bottom. The
  // moment they scroll up to read earlier content, we stop pinning
  // them to the bottom — a "Jump to latest" pill appears instead so
  // they can come back when ready.
  const messagesScrollRef = useRef(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      // 120 px hysteresis avoids flicker while streaming.
      const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 120;
      setPinnedToBottom(nearBottom);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!pinnedToBottom) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, pinnedToBottom]);

  const jumpToLatest = () => {
    setPinnedToBottom(true);
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Resume any in-flight document-generation job after a tab switch / reload.
  // We first check localStorage for a recent job, then verify against the
  // backend (which is the source of truth). If the job is still running, we
  // re-attach the polling loop so the progress card reappears.
  const docGenResumedRef = useRef(false);
  useEffect(() => {
    if (docGenResumedRef.current) return;
    docGenResumedRef.current = true;
    (async () => {
      try {
        // Server is source of truth — ignore stale localStorage if the job is gone
        const { data: jobs } = await portalDocumentsApi.listActive();
        if (!jobs || jobs.length === 0) {
          try { localStorage.removeItem('cognara.docgen.active'); } catch { /* localStorage may be unavailable */ }
          return;
        }
        const active = jobs[0];
        // Recover the original format object so the progress card icon/colour matches
        const fmt = DOC_FORMATS.find(f => f.id === active.format) || { id: active.format, label: active.format.toUpperCase(), color: '#1E4E8C' };
        let cached = null;
        try { cached = JSON.parse(localStorage.getItem('cognara.docgen.active') || 'null'); } catch { /* localStorage may be unavailable */ }
        const startedAt = (cached && cached.startedAt) || new Date(active.created_at).getTime();
        setDocGenProgress({ jobId: active.job_id, status: active.status, format: fmt, startedAt });
        setIsGeneratingDoc(true);
        const job = await pollDocGenJob(active.job_id, fmt, startedAt);
        await finalizeDocGenJob({ ...job, job_id: active.job_id }, fmt);
        setDocGenProgress(p => p ? { ...p, status: 'ready' } : p);
        setTimeout(() => setDocGenProgress(null), 1500);
        refetchConversations();
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
        console.warn('[docgen] resume failed:', msg);
        setDocGenProgress(null);
      } finally {
        setIsGeneratingDoc(false);
      }
    })();
  }, []);



  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 160) + 'px';
    }
  }, [input]);

  const handleNewChat = () => {
    clearMessages();
    resetStream();
    setUploadedFile(null);
    setUploadedText('');
    setSelectedFormat(null);
    navigate('/portal/chat');
    inputRef.current?.focus();
  };

  // File upload handler
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Binary MPP detected? Show the conversion-help modal FIRST. Server-
    // side parsing of .mpp depends on a Java runtime that's not always
    // present in production; surfacing the workflow up-front beats a
    // confusing error toast after the upload has already started.
    const lower = (file.name || '').toLowerCase();
    if (lower.endsWith('.mpp') || lower.endsWith('.mpd')) {
      setPendingMppFile(file);
      // Keep the file in the input so "Try anyway" can pick it back up;
      // we'll clear it on cancel.
      return;
    }

    // 3D model? Route through the APS asset pipeline instead of the text
    // extractor. The asset gets uploaded + translated in the background;
    // when it lands, the user can pop the viewer + AI rail open inline.
    if (is3DAsset(file.name)) {
      // No size cap — backend streams to disk + chunked S3 multipart so
      // files up to ~25 GB work without ingress / memory pressure.
      setIsUploading(true);
      try {
        const { data } = await portalAssetsApi.upload(file);
        toast.success(`Uploaded ${file.name} — translation started`);
        setInlineAsset(data); // {id, urn, filename, status:'uploading'}
        // Poll until ready/failed so the viewer button can light up.
        const poll = async () => {
          try {
            const r = await portalAssetsApi.get(data.id);
            setInlineAsset(r.data);
            if (['success', 'failed', 'timeout'].includes((r.data?.status || '').toLowerCase())) return;
          } catch { /* transient */ }
          setTimeout(poll, 5000);
        };
        poll();
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Upload failed');
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return;
    }

    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }

    await _doFileUpload(file);
  };

  // Extracted from handleFileUpload so the MPP conversion-help modal's
  // "Try uploading anyway" button can retry the same path. Encapsulates
  // the FormData build + auth header + extraction toast.
  const _doFileUpload = async (file) => {
    setIsUploading(true);
    try {
      const { token, authType } = usePortalStore.getState();
      const formData = new FormData();
      formData.append('file', file);
      const headers = {};
      if (token && authType === 'jwt') headers.Authorization = `Bearer ${token}`;
      const resp = await fetch(`${API_BASE}/api/portal/chat/upload`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: formData,
      });
      if (!resp.ok) throw new Error('Upload failed');
      const data = await resp.json();
      setUploadedFile({ name: file.name, size: file.size });
      setUploadedText(data.extracted_text || '');
      toast.success(`Uploaded ${file.name} (${(data.extracted_length / 1000).toFixed(1)}K chars extracted)`);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Document generation — polling loop. Extracted so it can be reused both
  // for the initial submit AND when resuming an in-flight job after a tab
  // switch / page reload. There is NO hard wall-clock cutoff: we poll until
  // the server reports `ready` or `failed`. The server already TTLs jobs
  // after 30 min, so a missing job is treated as terminal.
  const pollDocGenJob = useCallback(async (jobId, format, startedAt) => {
    let job = null;
    let transientFailures = 0;
    const MAX_TRANSIENT = 12; // ~30s of pain tolerated before bailing
    while (true) {
      await new Promise(r => setTimeout(r, 2500));
      try {
        const statusRes = await portalDocumentsApi.status(jobId);
        transientFailures = 0;
        job = statusRes.data;
        if (typeof window !== 'undefined') {
          console.log('[docgen.poll]', { status: job?.status, progress: job?.progress, progress_label: job?.progress_label, events_len: Array.isArray(job?.events) ? job.events.length : null });
          window.__lastPoll = job;
        }
        // Surface server-side stage to the progress card (drafting/building/etc.)
        // Also capture events on terminal states so the final activity entries
        // (e.g. "Wrote 5,016 rows · 100%") are visible in the log.
        if (job?.status) {
          setDocGenProgress(p => p ? {
            ...p,
            status: job.status,
            progressLabel: job.progress_label || p.progressLabel,
            progressRatio: typeof job.progress === 'number' ? job.progress : p.progressRatio,
            events: Array.isArray(job.events) ? job.events : p.events,
          } : p);
        }
        if (job?.status === 'ready') break;
        if (job?.status === 'cancelled') { setDocGenProgress(null); return null; }
        if (job?.status === 'failed') throw new Error(job.error || 'Generation failed');
      } catch (pollErr) {
        const code = pollErr?.response?.status;
        // 404 with a freshly-issued job is transient (backend hot-reload). But
        // a 404 after several successful polls means the job was TTL-pruned —
        // treat as a hard failure so we don't loop forever.
        const elapsedSec = (Date.now() - (startedAt || Date.now())) / 1000;
        const isTransient = !code || code >= 500 || (code === 404 && elapsedSec < 20);
        if (!isTransient) throw pollErr;
        transientFailures += 1;
        console.warn(`[docgen] transient poll failure (${code || 'network'}) — retry ${transientFailures}/${MAX_TRANSIENT}`);
        if (transientFailures >= MAX_TRANSIENT) {
          throw new Error('Server connection unstable — please retry');
        }
        await new Promise(r => setTimeout(r, Math.min(transientFailures * 1000, 5000)));
      }
    }
    return job;
  }, [setDocGenProgress]);

  // User-initiated cancel — instructs backend to abort the job and clears
  // the local progress card. The polling loop sees status=cancelled and exits.
  const stopDocGenJob = useCallback(async () => {
    const jobId = docGenProgress?.jobId;
    setDocGenProgress(null);
    setIsGeneratingDoc(false);
    if (!jobId) return;
    try {
      await portalDocumentsApi.cancel(jobId);
      addMessage({
        role: 'assistant',
        content: '_Document generation stopped by you._',
        docCancelled: true,
      });
    } catch (err) {
      console.warn('[docgen.cancel] failed:', err?.response?.status, err?.message);
    }
  }, [docGenProgress?.jobId, setDocGenProgress, addMessage]);

  // Resolve a completed job — convert the payload/file into a chat message.
  const finalizeDocGenJob = useCallback(async (job, format) => {
    if (job.payload) {
      const data = job.payload;
      if (data.type === 'infographic') {
        addMessage({
          role: 'assistant',
          content: `Here's your **${data.title || format.label}**.${data.summary ? `\n\n${data.summary}` : ''}`,
          docData: data,
        });
      } else {
        addMessage({
          role: 'assistant',
          content: data.type === 'mermaid'
            ? `Here's your ${format.label}:\n\n\`\`\`mermaid\n${data.code}\n\`\`\``
            : `**${data.title}**\n\n${data.sections?.map(s => `**${s.label}**: ${s.value}\n${s.description}`).join('\n\n') || ''}\n\n${data.summary || ''}`,
          docData: data,
        });
      }
    } else if (job.download_ready) {
      const dlRes = await portalDocumentsApi.download(job.job_id || job.id);
      const blob = dlRes.data;
      const url = URL.createObjectURL(blob);
      const ext = format.id;
      addMessage({
        role: 'assistant',
        content: `Your ${format.label} has been generated. Click below to download.`,
        downloadUrl: url,
        downloadName: `${job.title || 'generated_document'}.${ext}`,
        docFormat: format,
      });
    } else {
      throw new Error('Unexpected response from server');
    }
  }, [addMessage]);

  // Document generation handler
  const handleGenerateDoc = useCallback(async (format) => {
    if (!input.trim()) { toast.error('Enter a prompt for document generation'); return; }
    setIsGeneratingDoc(true);
    setSelectedFormat(null);
    setShowFormatMenu(false);

    const prompt = input.trim();
    setInput('');
    // Resolve the requested output scale: explicit numeric override wins,
    // else parse from prompt (e.g. "10,000 rows", "50k items", "30 page report").
    const parsedScale = parseScaleFromPrompt(prompt, format.id);
    const explicitScale = parseInt(String(targetScale).replace(/[^\d]/g, ''), 10);
    const effectiveScale = (Number.isFinite(explicitScale) && explicitScale > 0)
      ? Math.min(100_000, explicitScale)
      : (parsedScale || null);
    // Reset the override so a follow-up generate doesn't accidentally inherit it
    setTargetScale('');
    const scaleSuffix = effectiveScale
      ? ` (${effectiveScale.toLocaleString()} ${format.id === 'xlsx' ? 'rows' : format.id === 'docx' ? 'pages' : 'slides'})`
      : '';
    addMessage({ role: 'user', content: `[Generate ${format.label}${scaleSuffix}] ${prompt}` });

    const startedAt = Date.now();
    // Scope the progress to THIS conversation. Without this, switching to any
    // other chat (or opening a new chat) shows the same docgen card and blocks
    // the textarea — the bug the user reported.
    const docGenConvoId = conversationId || `new-${startedAt}`;
    setDocGenProgress({ status: 'starting', format, startedAt, targetScale: effectiveScale, conversationId: docGenConvoId });

    try {
      const chatContext = messages.slice(-6).map(m => `${m.role}: ${m.content}`).join('\n');

      // 1. Start async job — retry on transient 5xx/404 so a backend hot-reload
      // during the exact moment the user clicks Generate doesn't immediately fail.
      let startRes = null;
      let startAttempts = 0;
      const MAX_START_ATTEMPTS = 5;
      while (startAttempts < MAX_START_ATTEMPTS) {
        try {
          const payload = {
            prompt, format: format.id, context: chatContext,
            // Associate the job with the active conversation so the
            // Activity Timeline drawer can list it as audit history.
            conversation_id: activeConversationId || conversationId || undefined,
            // Pass user-selected engine so docgen honours the chat header
            // model selection (Opus 4.7 / Opus 4.6 / Sonnet 4.6).
            model: selectedModel || undefined,
            // Reply language (en/ja/ar) — backend injects a localisation
            // directive so generated DOCX/XLSX/PPTX content (titles,
            // headings, body, table headers) matches the chat language.
            language: language || 'en',
          };
          // Scale override — only one of these is needed; backend reads any of them
          if (effectiveScale) {
            if (format.id === 'xlsx') payload.target_rows = effectiveScale;
            else if (format.id === 'docx') payload.target_pages = effectiveScale;
            else if (format.id === 'pptx') payload.target_slides = effectiveScale;
          }
          // Uploaded file (PDF / DOCX / etc.) extracted to text by /chat/upload
          if (uploadedText && uploadedFile) {
            payload.context_files = [{ filename: uploadedFile.name, extracted_text: uploadedText }];
          }
          startRes = await portalDocumentsApi.start(payload);
          break;
        } catch (startErr) {
          const code = startErr?.response?.status;
          const isTransient = !code || code >= 500 || code === 404;
          startAttempts += 1;
          if (!isTransient || startAttempts >= MAX_START_ATTEMPTS) throw startErr;
          console.warn(`[docgen] start transient failure (${code || 'network'}) — retry ${startAttempts}/${MAX_START_ATTEMPTS}`);
          await new Promise(r => setTimeout(r, Math.min(startAttempts * 1200, 4000)));
        }
      }
      const jobId = startRes?.data?.job_id;
      if (!jobId) throw new Error('No job id returned from server');
      // Persist jobId immediately so the progress card survives tab switches
      setDocGenProgress({ jobId, status: 'pending', format, startedAt, conversationId: docGenConvoId });

      // 2. Poll until ready/failed (no wall-clock cutoff).
      const job = await pollDocGenJob(jobId, format, startedAt);

      // 3. Materialize the result into a chat message.
      await finalizeDocGenJob({ ...job, job_id: jobId }, format);

      setDocGenProgress(p => p ? { ...p, status: 'ready' } : p);
      setTimeout(() => setDocGenProgress(null), 1500);
      refetchConversations();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Unknown error';
      console.error('[docgen] failed:', err);
      setDocGenProgress(p => p ? { ...p, status: 'failed', error: msg } : p);
      addMessage({ role: 'assistant', content: `Failed to generate document: ${msg}` });
      setTimeout(() => setDocGenProgress(null), 4500);
    } finally {
      setIsGeneratingDoc(false);
    }
  }, [input, conversationId, addMessage, setDocGenProgress, pollDocGenJob, refetchConversations, selectedModel, language]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isStreaming) return;

    // /draw slash command — route to the Design Studio streaming
    // endpoint and render the SVG inline in an assistant bubble. Keeps
    // the design flow reachable without leaving Agent Chat.
    const trimmed = input.trim();
    if (/^\/(draw|design|floorplan)\s+/i.test(trimmed)) {
      const brief = trimmed.replace(/^\/(draw|design|floorplan)\s+/i, '');
      setInput('');
      addMessage({ role: 'user', content: trimmed });
      addMessage({ role: 'assistant', content: '', streaming: true, kind: 'design', svgBuffer: '' });
      try {
        const form = new FormData();
        form.append('prompt', brief);
        form.append('output_format', 'svg');   // in-chat is SVG-only for speed
        form.append('size', '1200x800');
        const token = usePortalStore.getState().token;
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portal/design/stream`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form,
        });
        if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        let svg = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const frames = buf.split('\n\n');
          buf = frames.pop() || '';
          for (const f of frames) {
            let ev = 'message', dat = null;
            for (const line of f.split('\n')) {
              if (line.startsWith('event:')) ev = line.slice(6).trim();
              else if (line.startsWith('data:')) { try { dat = JSON.parse(line.slice(5).trim()); } catch (_e) { /* ignore malformed frame */ } }
            }
            if (ev === 'svg_delta' && dat) {
              svg += dat.delta || '';
              const cs = useChatStore.getState();
              const list = cs.messages.slice();
              const last = list[list.length - 1];
              if (last && last.kind === 'design') {
                list[list.length - 1] = { ...last, svgBuffer: svg };
                cs.setMessages(list);
              }
            }
          }
        }
        const cs = useChatStore.getState();
        const list = cs.messages.slice();
        const last = list[list.length - 1];
        if (last && last.kind === 'design') {
          list[list.length - 1] = { ...last, streaming: false, svgBuffer: svg };
          cs.setMessages(list);
        }
      } catch (e) {
        toast.error(`/draw failed: ${e.message}`);
        const cs = useChatStore.getState();
        const list = cs.messages.slice();
        list[list.length - 1] = { role: 'assistant', content: `Sorry — /draw failed: ${e.message}` };
        cs.setMessages(list);
      }
      return;
    }

    // If a format is selected, trigger doc generation instead
    if (selectedFormat) {
      handleGenerateDoc(selectedFormat);
      return;
    }

    let userContent = input.trim();
    // If a file was uploaded, inject its context
    if (uploadedText) {
      userContent = `${userContent}\n\n<uploaded_document filename="${uploadedFile?.name}">\n${uploadedText.slice(0, 20000)}\n</uploaded_document>`;
    }

    const userMessage = { role: 'user', content: input.trim(), attachment: uploadedFile ? { name: uploadedFile.name } : null };
    const allMessages = [...messages, { role: 'user', content: userContent }];

    setInput('');
    setUploadedFile(null);
    setUploadedText('');
    addMessage(userMessage);
    resetStream();

    // Kick a refetch of the conversations list right now so the
    // active chat bubbles to the top of the sidebar while the stream
    // is still running (don't wait for the response to finish).
    setTimeout(() => { try { refetchConversations(); } catch {} }, 800);

    try {
      const result = await streamMessage(
        allMessages.map(m => ({ role: m.role, content: m.content })),
        activeConversationId,
        selectedModel,
        selectedKbIds,
        language,
      );

      // Streaming done — commit the assistant message
      addMessage({
        role: 'assistant',
        content: (result.content || '') + (result.aborted ? '\n\n_— stopped by you_' : ''),
        sources: result.sources,
        toolCalls: result.toolCalls,
        aborted: result.aborted || false,
        partial: result.aborted || false,
        kbScope: result.kbScope || (selectedKbIds.length ? [...selectedKbIds] : null),
        meter: result.meter || null,
      });

      // M6 Insight Reveal — surface a particle toast when the response has
      // produced a meaningful signal (tools invoked, or memory compressed).
      try {
        const firstLine = (result.content || '').trim().split(/\n|\. /)[0]?.slice(0, 140);
        const toolNames = (result.toolCalls || []).map(tc => tc?.tool).filter(Boolean);
        if (toolNames.length > 0 && firstLine) {
          setInsight({
            id: `tool-${Date.now()}`,
            kind: 'tool',
            headline: firstLine,
            detail: `Invoked ${toolNames.length} tool${toolNames.length > 1 ? 's' : ''}: ${toolNames.slice(0, 3).join(', ')}`,
          });
        } else if (result.memory?.compressed) {
          setInsight({
            id: `memory-${Date.now()}`,
            kind: 'memory',
            headline: 'Older turns compressed into a running summary.',
            detail: `${result.memory.messages_compressed || 0} messages consolidated · recent window preserved`,
          });
        }
      } catch { /* insight is cosmetic — never break the chat flow */ }

      if (result.conversationId && !activeConversationId) {
        setActiveConversation(result.conversationId);
        navigate(`/portal/chat/${result.conversationId}`, { replace: true });
      }
      refetchConversations();
    } catch {
      // Error already toasted by useStreamChat
    }
  }, [input, isStreaming, selectedFormat, selectedModel, messages, activeConversationId, streamMessage, addMessage, resetStream, setActiveConversation, navigate, refetchConversations, uploadedText, uploadedFile, selectedKbIds, language]);

  // Edit a prior user message — truncates history at that index, replaces the
  // edited content, re-streams from there. Assistant responses after the edit
  // point are discarded (the ones being regenerated).
  const handleEditMessage = useCallback(async (index, newContent) => {
    if (isStreaming) return;
    const truncated = messages.slice(0, index);
    const edited = { ...messages[index], content: newContent, attachment: null };
    const nextMessages = [...truncated, edited];

    setMessages(nextMessages);
    resetStream();

    try {
      const result = await streamMessage(
        nextMessages.map(m => ({ role: m.role, content: m.content })),
        activeConversationId,
        selectedModel,
        selectedKbIds,
        language,
      );
      addMessage({
        role: 'assistant',
        content: (result.content || '') + (result.aborted ? '\n\n_— stopped by you_' : ''),
        sources: result.sources,
        toolCalls: result.toolCalls,
        aborted: result.aborted || false,
        partial: result.aborted || false,
        kbScope: result.kbScope || (selectedKbIds.length ? [...selectedKbIds] : null),
        meter: result.meter || null,
      });
      if (result.conversationId && !activeConversationId) {
        setActiveConversation(result.conversationId);
        navigate(`/portal/chat/${result.conversationId}`, { replace: true });
      }
      refetchConversations();
    } catch {
      // Error already toasted by useStreamChat
    }
  }, [isStreaming, messages, setMessages, resetStream, streamMessage, activeConversationId, selectedModel, addMessage, setActiveConversation, navigate, refetchConversations, selectedKbIds, language]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Retry an assistant response — pop it off (and any later messages) and
  // re-stream from the most-recent user turn that preceded it.
  const handleRetry = useCallback(async (assistantIndex) => {
    if (isStreaming) return;
    const upTo = messages.slice(0, assistantIndex);  // drop the assistant + anything after
    // Walk backward to the most recent user message
    let userIdx = -1;
    for (let i = upTo.length - 1; i >= 0; i--) {
      if (upTo[i].role === 'user') { userIdx = i; break; }
    }
    if (userIdx < 0) return;
    const truncated = upTo.slice(0, userIdx + 1);
    setMessages(truncated);
    resetStream();
    try {
      const result = await streamMessage(
        truncated.map(m => ({ role: m.role, content: m.content })),
        activeConversationId,
        selectedModel,
        selectedKbIds,
        language,
      );
      addMessage({
        role: 'assistant',
        content: (result.content || '') + (result.aborted ? '\n\n_— stopped by you_' : ''),
        sources: result.sources,
        toolCalls: result.toolCalls,
        aborted: result.aborted || false,
        partial: result.aborted || false,
        kbScope: result.kbScope || (selectedKbIds.length ? [...selectedKbIds] : null),
        meter: result.meter || null,
      });
      refetchConversations();
    } catch { /* toast handled in hook */ }
  }, [isStreaming, messages, setMessages, resetStream, streamMessage, activeConversationId, selectedModel, addMessage, refetchConversations, selectedKbIds, language]);

  // Submit thumbs-up/down feedback for a specific assistant message
  const handleFeedback = useCallback(async (assistantIndex, payload) => {
    const cid = activeConversationId || conversationId;
    if (!cid) return; // feedback only on saved conversations
    try {
      await portalConversationsApi.feedback(cid, {
        message_index: assistantIndex,
        vote: payload.vote,
        comment: payload.comment || null,
      });
      toast.success(payload.vote === 'up' ? 'Thanks for the feedback!' : 'Feedback recorded');
    } catch {
      toast.error('Could not save feedback');
    }
  }, [activeConversationId, conversationId]);

  // Command-bar "Ask Cognara" — pre-seed input from ?q= and auto-send
  const seededRef = useRef(false);
  const pendingSendRef = useRef(false);
  // When the user clicks "Send to Knowledge Chat" inside the APS Asset
  // drawer, we redirect them to /portal/chat?conversation={id}. Route
  // them into the seeded conversation here so they land on the thread
  // with the BoQ / schedule / clash transcript already loaded.
  useEffect(() => {
    const cid = searchParams.get('conversation');
    if (cid && cid !== conversationId) {
      setSearchParams({}, { replace: true });
      navigate(`/portal/chat/${cid}`);
    }
  }, [searchParams, conversationId, navigate, setSearchParams]);

  useEffect(() => {
    if (seededRef.current || conversationId) return;
    const q = searchParams.get('q');
    if (!q) return;
    seededRef.current = true;
    setInput(q);
    setSearchParams({}, { replace: true });
    pendingSendRef.current = true;
  }, [conversationId, searchParams, setSearchParams]);

  // Fires once the seeded input has flushed to state and handleSend sees it
  useEffect(() => {
    if (pendingSendRef.current && input && !isStreaming) {
      pendingSendRef.current = false;
      handleSend();
    }
  }, [input, isStreaming, handleSend]);

  // Filter conversations by search, then keep newest first (defensive
  // client-side sort so ties / cached lists / optimistic updates always
  // bubble the most-recently-updated chat to the top).
  const filteredConversations = conversations
    .filter(c => !searchQuery || c.title?.toLowerCase().includes(searchQuery.toLowerCase()))
    .slice()
    .sort((a, b) => {
      const ta = new Date(a.updated_at || a.created_at || 0).getTime();
      const tb = new Date(b.updated_at || b.created_at || 0).getTime();
      return tb - ta;
    });

  // Build display messages — when streaming, surface the in-flight assistant
  // tokens as a temporary bubble so the user sees content rendering in real
  // time. We append a placeholder assistant bubble if one doesn't exist yet
  // (the persisted assistant message is only added after the stream finishes).
  const displayMessages = [...messages];
  if (isStreaming && streamingContent) {
    const lastIdx = displayMessages.length - 1;
    if (lastIdx >= 0 && displayMessages[lastIdx].role === 'assistant') {
      displayMessages[lastIdx] = { ...displayMessages[lastIdx], content: streamingContent };
    } else {
      displayMessages.push({ role: 'assistant', content: streamingContent });
    }
  }

  return (
    <div className="flex h-full overflow-hidden" data-testid="portal-chat">
      {/* Conversation Sidebar */}
      <AnimatePresence mode="wait">
        {sidebarOpen && (
          <motion.aside
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="flex-shrink-0 border-r border-nexus-border-subtle flex flex-col bg-nexus-bg-primary/50 overflow-hidden"
            data-testid="chat-sidebar"
          >
            {/* New Chat + Search */}
            <div className="p-3 space-y-2 border-b border-nexus-border-subtle">
              <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleNewChat}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-gradient-to-r from-nexus-accent-mint/20 to-nexus-accent-mint/5 border border-nexus-accent-mint/20 text-nexus-accent-mint text-sm font-medium hover:from-nexus-accent-mint/25 transition-all"
                data-testid="new-chat-btn"
              >
                <Plus size={16} />
                New Chat
              </motion.button>

              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-nexus-text-tertiary pointer-events-none" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search chats..."
                  className="chat-sidebar-search w-full rounded-lg pl-8 pr-3 py-2 text-xs focus:outline-none transition-colors"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid var(--hairline, rgba(255,255,255,0.08))',
                    color: 'var(--nexus-text-primary, #E5E7EB)',
                    WebkitBoxShadow: '0 0 0 1000px transparent inset',
                    WebkitTextFillColor: 'currentColor',
                    caretColor: 'currentColor',
                  }}
                  data-testid="chat-search"
                />
              </div>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
              {filteredConversations.map((convo, i) => (
                <ConversationItem
                  key={convo.id}
                  convo={convo}
                  index={i}
                  isActive={conversationId === convo.id || activeConversationId === convo.id}
                  onSelect={() => navigate(`/portal/chat/${convo.id}`)}
                  onRenamed={refetchConversations}
                  onDeleted={() => {
                    refetchConversations();
                    if (conversationId === convo.id) navigate('/portal/chat');
                  }}
                />
              ))}

              {filteredConversations.length === 0 && (
                <div className="text-center py-10 text-nexus-text-tertiary">
                  <MessageSquare size={24} className="mx-auto mb-2 opacity-40" />
                  <p className="text-xs">{searchQuery ? 'No matches' : 'No conversations yet'}</p>
                </div>
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar — sticky so it stays pinned when the outer
            <main> scroll container scrolls under it. Solid background
            + tiny shadow / hairline so streaming content doesn't show
            through. */}
        <div
          className="sticky top-0 z-30 h-12 flex items-center px-4 border-b border-nexus-border-subtle flex-shrink-0 backdrop-blur"
          style={{ background: 'color-mix(in srgb, var(--nexus-bg-primary) 92%, transparent)' }}
          data-testid="chat-top-bar"
        >
          <button
            onClick={() => setSidebarOpen(prev => !prev)}
            className="p-1.5 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors mr-3"
            aria-label={sidebarOpen ? 'Collapse conversation sidebar' : 'Expand conversation sidebar'}
            aria-expanded={sidebarOpen}
            data-testid="toggle-sidebar-btn"
          >
            {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-nexus-text-primary truncate" data-testid="chat-header-title">
              {(() => {
                const t = (conversationData?.title || '').trim();
                if (t && t !== 'New Chat') return t;
                // Fall back to the first user message snippet so the header
                // never shows "New Chat" once a question has been asked.
                const firstUser = messages.find((m) => m.role === 'user');
                const snippet = (firstUser?.content || '').trim();
                if (snippet) return snippet.slice(0, 60) + (snippet.length > 60 ? '…' : '');
                return 'New Chat';
              })()}
            </p>
          </div>
          {/* Conversation running total — sums up the `meter.deducted`
              field stamped on each assistant reply. Gives engineering
              teams a rolling cost-of-conversation view for budgeting
              long Apex 4.8 threads. */}
          {(() => {
            const totals = messages.reduce((acc, m) => {
              if (m.role !== 'assistant' || !m.meter) return acc;
              const d = Number(m.meter.deducted || 0);
              acc.credits += d;
              acc.replies += 1;
              return acc;
            }, { credits: 0, replies: 0 });
            if (totals.replies === 0) return null;
            const cr = totals.credits;
            const display = cr >= 100 ? cr.toFixed(0) : cr.toFixed(1);
            return (
              <div className="mr-3 hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                   style={{ background: 'rgba(242,107,31,0.08)', border: '1px solid rgba(242,107,31,0.22)' }}
                   data-testid="conversation-credit-total"
                   title={`This conversation has consumed ${cr.toFixed(2)} credits across ${totals.replies} repl${totals.replies === 1 ? 'y' : 'ies'}.`}>
                <span className="ig-eyebrow text-[9px]" style={{ color: '#F26B1F' }}>This thread</span>
                <span className="ig-mono text-[9px] font-semibold text-nexus-text-secondary">{display} cr</span>
              </div>
            );
          })()}
          {/* Cognara agent presence — shows the active model in shorthand */}
          {(() => {
            const shortModel = selectedModel === 'claude-opus-4-8' ? 'Apex 4.8'
              : selectedModel === 'claude-opus-4-7' ? 'Ultra 4.7'
              : selectedModel === 'claude-opus-4-6' ? 'Pro 4.6'
              : (selectedModel || '').replace('claude-', '').replace(/-/g, ' ');
            return (
              <div className="mr-3 hidden sm:inline-flex items-center gap-2 px-2.5 py-1 rounded-full"
                   style={{ background: 'rgba(30,78,140,0.08)', border: '1px solid rgba(30,78,140,0.2)' }}
                   data-testid="agent-presence"
                   title={`Cognara Engine · ${shortModel}`}>
                <span className="ig-live-dot" style={{ width: 6, height: 6 }} />
                <span className="ig-eyebrow text-[9px]" style={{ color: 'var(--nexus-accent-blue)' }}>Cognara Engine</span>
                <span className="ig-mono text-[9px] text-nexus-text-tertiary">{shortModel}</span>
              </div>
            );
          })()}
          {/* Memory indicator — always visible on saved conversations so users can pin facts anytime */}
          {conversationId && (() => {
            const compressedCount = memory?.messages_compressed ?? conversationData?.summary_cursor ?? 0;
            const hasSummary = memory?.compressed || !!conversationData?.summary;
            const pinCount = (conversationData?.pinned_facts || []).length;
            const isActive = hasSummary || pinCount > 0;
            const label = hasSummary && compressedCount > 0
              ? (pinCount > 0
                ? `Memory · ${compressedCount} compressed · ${pinCount} pinned`
                : `Summarized earlier turns · ${compressedCount} messages compressed`)
              : pinCount > 0
                ? `${pinCount} pinned fact${pinCount !== 1 ? 's' : ''}`
                : 'Memory';
            return (
              <motion.button
                type="button"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                onClick={() => setMemoryDrawerOpen(true)}
                className={`mr-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors cursor-pointer ${
                  isActive
                    ? 'bg-nexus-accent-mint/10 text-nexus-accent-mint border-nexus-accent-mint/20 hover:bg-nexus-accent-mint/20 hover:border-nexus-accent-mint/40'
                    : 'bg-nexus-bg-tertiary/40 text-nexus-text-tertiary border-nexus-border-subtle hover:text-nexus-accent-mint hover:border-nexus-accent-mint/30'
                }`}
                title="Click to view memory and pin facts"
                aria-label={`Open memory drawer — ${label}`}
                data-testid="memory-pill"
              >
                <Brain size={12} className="flex-shrink-0" />
                <span className="whitespace-nowrap">{label}</span>
              </motion.button>
            );
          })()}
          {/* Export Dropdown */}
          {conversationId && (
            <ExportDropdown conversationId={conversationId} title={conversationData?.title || 'conversation'} />
          )}
          {/* Activity (DocGen audit log) — only meaningful on saved chats */}
          {conversationId && (
            <button
              type="button"
              onClick={() => setActivityDrawerOpen(true)}
              className="ml-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border bg-nexus-bg-tertiary/40 text-nexus-text-tertiary border-nexus-border-subtle hover:text-nexus-accent-mint hover:border-nexus-accent-mint/30 transition-colors"
              title="Document activity — every docgen job in this conversation"
              data-testid="docgen-activity-open"
            >
              <ClipboardList size={12} />
              <span>Activity</span>
            </button>
          )}
        </div>

        {/* Messages — wider, calmer reading column for the new
            zoomed-out layout. Larger horizontal padding and a
            generous max-width centre the content like ChatGPT/Claude. */}
        <div
          ref={messagesScrollRef}
          className="flex-1 overflow-y-auto px-4 md:px-8 lg:px-12 py-8 relative"
        >
          {displayMessages.length === 0 && !isStreaming ? (
            <EmptyChat
              onPromptClick={(prompt) => {
                setInput(prompt);
                inputRef.current?.focus();
              }}
            />
          ) : (
            <div
              className="max-w-6xl mx-auto space-y-7"
              data-testid="messages-list"
              dir={language === 'ar' ? 'rtl' : 'ltr'}
            >
              {displayMessages.map((msg, i) => (
                <div key={`${i}-${msg.role}`}>
                  {/* "Answering from N KB(s)" chip — rendered above the
                      assistant bubble whenever the user explicitly scoped
                      retrieval to one or more knowledge bases for this turn. */}
                  {msg.role === 'assistant' && Array.isArray(msg.kbScope) && msg.kbScope.length > 0 && (() => {
                    const names = msg.kbScope.map(id => kbList.find(k => k.id === id)?.name).filter(Boolean);
                    const display = names.length
                      ? (names.length <= 2 ? names.join(' · ') : `${names.slice(0, 2).join(' · ')} +${names.length - 2}`)
                      : `${msg.kbScope.length} KB${msg.kbScope.length === 1 ? '' : 's'}`;
                    return (
                      <div className="ml-10 mb-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider text-nexus-accent-mint bg-nexus-accent-mint/10 border border-nexus-accent-mint/25"
                           data-testid={`kb-scope-chip-${i}`}
                           title={`Answering only from: ${names.join(', ') || msg.kbScope.join(', ')}`}>
                        <Database size={11} strokeWidth={2.5} />
                        Answering from {display}
                      </div>
                    );
                  })()}
                  <NexusChatBubble
                    role={msg.role}
                    content={msg.content}
                    partial={!!msg.partial}
                    sources={msg.sources || (i === displayMessages.length - 1 && !isStreaming ? sources : undefined)}
                    toolCalls={msg.toolCalls || (i === displayMessages.length - 1 && !isStreaming ? toolCalls : undefined)}
                    isStreaming={isStreaming && i === displayMessages.length - 1 && msg.role === 'assistant'}
                    streamProgress={
                      isStreaming && i === displayMessages.length - 1 && msg.role === 'assistant'
                        ? { chars: (msg.content || '').length, startedAt: streamStartedAt }
                        : null
                    }
                    onEditSave={msg.role === 'user' ? (newContent) => handleEditMessage(i, newContent) : undefined}
                    onFeedback={msg.role === 'assistant' ? (payload) => handleFeedback(i, payload) : undefined}
                    onRetry={msg.role === 'assistant' && !isStreaming ? () => handleRetry(i) : undefined}
                  />
                  {/* /draw inline SVG preview — Design Studio streaming
                      rendered directly inside the chat bubble so the
                      user can generate drawings without leaving Agent Chat. */}
                  {msg.kind === 'design' && (() => {
                    let sliced = msg.svgBuffer || '';
                    const idx = sliced.indexOf('<svg');
                    if (idx >= 0) {
                      sliced = sliced.slice(idx);
                      if (!sliced.includes('</svg>')) sliced += '</svg>';
                    } else {
                      sliced = '';
                    }
                    // Force the SVG to fit its container by stripping the
                    // explicit width/height attributes emitted by the model
                    // and letting CSS scale it responsively.
                    const displaySvg = sliced.replace(/<svg\b([^>]*)>/i, (_, attrs) => {
                      let a = attrs
                        .replace(/\swidth\s*=\s*(['"]).*?\1/i, '')
                        .replace(/\sheight\s*=\s*(['"]).*?\1/i, '');
                      if (!/preserveAspectRatio/i.test(a)) a += ' preserveAspectRatio="xMidYMid meet"';
                      return `<svg${a} width="100%" height="auto" style="max-width:100%;height:auto;display:block">`;
                    });
                    return (
                      <div className="mt-2 rounded-lg overflow-hidden p-3 relative"
                        style={{ background: '#FFFFFF', border: '1px solid var(--hairline)', minHeight: 200 }}
                        data-testid={`chat-design-${i}`}>
                        {sliced ? (
                          <div dangerouslySetInnerHTML={{ __html: displaySvg }} className="w-full [&>svg]:w-full [&>svg]:h-auto" />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-40 gap-2 text-nexus-text-tertiary">
                            <Loader2 size={20} className="animate-spin" style={{ color: '#F26B1F' }} />
                            <span className="text-[11px]">Cognara Apex is drafting…</span>
                          </div>
                        )}
                        {msg.streaming && sliced && (
                          <div className="absolute top-2 right-2 text-[10px] font-mono px-2 py-0.5 rounded"
                            style={{ background: 'rgba(242,107,31,0.15)', color: '#F26B1F' }}>
                            {(msg.svgBuffer || '').length.toLocaleString()} chars
                          </div>
                        )}
                        {!msg.streaming && sliced && (
                          <button
                            onClick={() => {
                              const blob = new Blob([sliced], { type: 'image/svg+xml' });
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = `cognara_chat_${i}.svg`; a.click();
                              URL.revokeObjectURL(url);
                            }}
                            className="absolute top-2 right-2 text-[10px] px-2 py-0.5 rounded font-semibold hover:opacity-80"
                            style={{ background: '#F26B1F', color: '#fff' }}
                            data-testid={`chat-design-download-${i}`}>
                            Download SVG
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {/* Credit-deduction receipt chip — shows users exactly
                      what each reply cost so they trust the metering and
                      can self-correlate against the wallet ledger. */}
                  {msg.role === 'assistant' && msg.meter && !isStreaming && (() => {
                    const m = msg.meter;
                    const tier = m.model === 'claude-opus-4-8' ? 'Apex 4.8'
                      : m.model === 'claude-opus-4-7' ? 'Ultra 4.7'
                      : m.model === 'claude-opus-4-6' ? 'Pro 4.6'
                      : (m.model || '').replace('claude-', '').replace(/-/g, ' ');
                    const deducted = Number(m.deducted ?? 0);
                    const base = Number(m.base_credits ?? 0);
                    const mult = Number(m.multiplier ?? 1);
                    return (
                      <div className="ml-10 mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono text-nexus-text-tertiary hover:text-nexus-text-secondary transition-colors"
                           style={{ background: 'rgba(242,107,31,0.06)', border: '1px solid rgba(242,107,31,0.18)' }}
                           data-testid={`credit-meter-chip-${i}`}
                           title={`Deducted ${deducted} credit${deducted === 1 ? '' : 's'} = ⌈${m.tokens_total || 0} tokens / 1000⌉ (${base} base) × ${mult}× (${tier})`}>
                        <span style={{ color: '#F26B1F', fontWeight: 600 }}>−{deducted} cr</span>
                        <span className="opacity-60">·</span>
                        <span>{tier}</span>
                        <span className="opacity-60">·</span>
                        <span>{mult.toFixed(1)}×</span>
                      </div>
                    );
                  })()}
                  {/* "Open HTML Preview" CTA — appears below assistant
                      bubbles that contain a detectable HTML artifact. */}
                  {msg.role === 'assistant' && msg.content && (() => {
                    const art = extractHtmlArtifact(msg.content);
                    if (!art) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => { setActiveArtifact({ ...art, conversationId }); setArtifactOpen(true); }}
                        className="ml-10 mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-transform hover:scale-[1.02]"
                        style={{ background: 'linear-gradient(135deg, #6BA6F5, #1E4E8C)' }}
                        title="Open interactive preview"
                        data-testid={`open-html-artifact-${i}`}
                      >
                        <Eye size={12} />
                        <span>Open Preview</span>
                        <span className="text-[9px] uppercase tracking-wider opacity-70">·&nbsp;{art.title}</span>
                      </button>
                    );
                  })()}
                  {/* "Cancelled" chip for stopped DocGen jobs. Detect both
                      the in-memory flag (just-cancelled by user) and the
                      historical content marker (rehydrated from MongoDB). */}
                  {msg.role === 'assistant' && (msg.docCancelled || /^_Document generation stopped by you\._?$/.test((msg.content || '').trim())) && (
                    <div className="ml-10 mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider text-amber-300/85 bg-amber-500/10 border border-amber-400/25"
                         data-testid={`docgen-cancelled-${i}`}
                         title="The user cancelled this document generation — no file was produced.">
                      <CircleSlash size={11} strokeWidth={2.5} />
                      DocGen Cancelled
                    </div>
                  )}
                  {/* Download card for generated documents */}
                  {msg.downloadUrl && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="ml-10 mt-2">
                      <a href={msg.downloadUrl} download={msg.downloadName}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium text-white transition-colors"
                        style={{ background: msg.docFormat?.color || '#1E4E8C' }}
                        data-testid={`download-doc-${i}`}>
                        <FileDown size={14} />
                        Download {msg.downloadName}
                      </a>
                    </motion.div>
                  )}
                  {/* Rich infographic card */}
                  {msg.docData?.type === 'infographic' && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="ml-10 mt-3"
                      data-testid={`infographic-${i}`}
                    >
                      <InfographicRenderer data={msg.docData} />
                    </motion.div>
                  )}
                  {/* Attachment badge */}
                  {msg.attachment && (
                    <div className="flex justify-end mt-1 mr-2">
                      <span className="inline-flex items-center gap-1 text-[10px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-md">
                        <Paperclip size={9} /> {msg.attachment.name}
                      </span>
                    </div>
                  )}
                </div>
              ))}

              {/* Active Tool Execution Cards */}
              <AnimatePresence>
                {isStreaming && activeTools.length > 0 && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-6xl mx-auto space-y-2">
                    {/* M6 Wow — Live orchestration graph while tools run */}
                    <div className="flex justify-center mb-1" aria-hidden="true">
                      <AgentOrchestrationGraph
                        active
                        activeAgent="karyakarta"
                        tools={activeTools.map((t) => t.tool).filter(Boolean)}
                        size="compact"
                      />
                    </div>
                    {activeTools.map((t, i) => (
                      <motion.div
                        key={`${t.tool}-${i}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`flex items-start gap-3 p-3 rounded-xl border text-sm ${
                          t.status === 'running'
                            ? 'bg-nexus-accent-mint/5 border-nexus-accent-mint/20'
                            : t.status === 'success'
                            ? 'bg-green-500/5 border-green-500/20'
                            : 'bg-red-500/5 border-red-500/20'
                        }`}
                        data-testid={`tool-card-${t.tool}`}
                      >
                        <div className={`mt-0.5 ${t.status === 'running' ? 'animate-spin' : ''}`}>
                          {t.status === 'running' ? (
                            <Loader2 size={16} className="text-nexus-accent-mint" />
                          ) : t.status === 'success' ? (
                            <Sparkles size={16} className="text-green-400" />
                          ) : (
                            <X size={16} className="text-red-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-nexus-text-primary text-xs">
                            {t.status === 'running' ? 'Calling' : t.status === 'success' ? 'Called' : 'Failed'}: {t.tool}
                          </p>
                          {t.args && (
                            <p className="text-[11px] text-nexus-text-tertiary truncate mt-0.5">
                              Args: {JSON.stringify(t.args).slice(0, 80)}
                            </p>
                          )}
                          {t.result && (
                            <p className="text-[11px] text-nexus-text-secondary mt-1 line-clamp-2">
                              {t.result.slice(0, 120)}
                            </p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Typing indicator when streaming hasn't produced content yet */}
              <AnimatePresence>
                {isStreaming && !streamingContent && displayMessages[displayMessages.length - 1]?.role === 'user' && (
                  <NexusTypingIndicator />
                )}
              </AnimatePresence>

              {/* Live document-generation progress card — only in the
                  conversation that started the job, not in others. */}
              <AnimatePresence>
                {docGenProgress && docGenInThisConvo && (
                  <DocGenProgressCard
                    key="docgen-progress"
                    status={docGenProgress.status}
                    format={docGenProgress.format}
                    startedAt={docGenProgress.startedAt}
                    error={docGenProgress.error}
                    progressLabel={docGenProgress.progressLabel}
                    progressRatio={docGenProgress.progressRatio}
                    targetScale={docGenProgress.targetScale}
                    events={docGenProgress.events}
                  />
                )}
              </AnimatePresence>

              <div ref={messagesEndRef} />
            </div>
          )}

          {/* "Jump to latest" pill — only shown when the user has
              scrolled up while a stream / new message is in flight. */}
          <AnimatePresence>
            {!pinnedToBottom && (
              <motion.button
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 12 }}
                onClick={jumpToLatest}
                className="absolute left-1/2 -translate-x-1/2 bottom-4 z-10 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white shadow-lg"
                style={{ background: 'linear-gradient(135deg, #6BA6F5, #1E4E8C)' }}
                data-testid="jump-to-latest"
                title="Resume auto-scroll"
              >
                <ChevronDown size={12} /> Jump to latest
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Input Area — match the wider, calmer reading column above
            so the composer aligns with the messages and breathes more. */}
        <div className="px-4 md:px-8 lg:px-12 pb-4 pt-2 border-t border-nexus-border-subtle">
          <div className="max-w-6xl mx-auto">
            {/* Upload/Format badges */}
            <AnimatePresence>
              {(uploadedFile || selectedFormat || inlineAsset) && (
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className="flex items-center gap-2 mb-2 flex-wrap">
                  {inlineAsset && (() => {
                    const st = (inlineAsset.status || 'pending').toLowerCase();
                    const ready = st === 'success';
                    const failed = ['failed', 'timeout'].includes(st);
                    return (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border"
                        style={{
                          background: ready ? 'rgba(16,185,129,0.10)' : failed ? 'rgba(239,68,68,0.10)' : 'rgba(30,78,140,0.12)',
                          color: ready ? '#34D399' : failed ? '#F87171' : '#7AB7FF',
                          borderColor: ready ? 'rgba(16,185,129,0.30)' : failed ? 'rgba(239,68,68,0.30)' : 'rgba(30,78,140,0.30)',
                        }}
                        data-testid="inline-asset-badge"
                      >
                        <Cuboid size={12} />
                        {inlineAsset.filename}
                        {ready ? (
                          <button onClick={() => setShowAssetPanel(true)} className="ml-1 underline opacity-90 hover:opacity-100" data-testid="open-inline-asset">
                            Open viewer + AI
                          </button>
                        ) : failed ? (
                          <span className="ml-1 opacity-90">translation failed</span>
                        ) : (
                          <span className="ml-1 inline-flex items-center gap-1 opacity-90">
                            <Loader2 size={10} className="animate-spin" />
                            {inlineAsset.progress || 'translating…'}
                          </span>
                        )}
                        <button onClick={() => setInlineAsset(null)} className="ml-1 opacity-60 hover:opacity-100"><X size={10} /></button>
                      </span>
                    );
                  })()}
                  {uploadedFile && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg" data-testid="uploaded-file-badge">
                      <Paperclip size={12} />
                      {uploadedFile.name}
                      <button onClick={() => { setUploadedFile(null); setUploadedText(''); }} className="ml-1 hover:text-blue-200"><X size={10} /></button>
                    </span>
                  )}
                  {selectedFormat && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg border" style={{ background: `${selectedFormat.color}15`, color: selectedFormat.color, borderColor: `${selectedFormat.color}30` }} data-testid="format-badge">
                      <selectedFormat.icon size={12} />
                      Generate: {selectedFormat.label}
                      <button onClick={() => setSelectedFormat(null)} className="ml-1 opacity-60 hover:opacity-100"><X size={10} /></button>
                    </span>
                  )}
                  {selectedFormat && (() => {
                    // Live scale hint — user typed "10,000 rows" or set the override
                    const explicit = parseInt(String(targetScale).replace(/[^\d]/g, ''), 10);
                    const parsed = parseScaleFromPrompt(input, selectedFormat.id);
                    const n = Number.isFinite(explicit) && explicit > 0 ? explicit : parsed;
                    if (!n) return null;
                    const unit = selectedFormat.id === 'xlsx' ? 'rows' : selectedFormat.id === 'docx' ? 'pages' : selectedFormat.id === 'pptx' ? 'slides' : 'items';
                    const source = Number.isFinite(explicit) && explicit > 0 ? 'override' : 'detected';
                    return (
                      <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-lg border border-nexus-border-subtle bg-nexus-bg-tertiary/40 text-nexus-text-tertiary" data-testid="scale-hint-badge" title={`Scale ${source}: ${n.toLocaleString()} ${unit}`}>
                        <span className="ig-mono">{n.toLocaleString()}</span>
                        <span>{unit}</span>
                        <span className="opacity-50">· {source}</span>
                      </span>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="relative bg-nexus-bg-secondary/60 border border-nexus-border-subtle rounded-2xl focus-within:border-nexus-accent-mint/30 transition-colors backdrop-blur-sm">
              {/* Toolbar row */}
              <div ref={composerToolbarRef} className="flex items-center gap-1 px-2 pt-2">
                {/* File upload */}
                <input ref={fileInputRef} type="file" accept=".pdf,.docx,.xlsx,.pptx,.txt,.csv,.md,.mpp,.mpx,.mpd,.mspdi,.xml,.xer,.rvt,.rfa,.ifc,.dwg,.dxf,.dwf,.nwd,.nwc,.3dm,.obj,.fbx,.gltf,.glb,.stp,.step,.iges,.skp,.f3d,.iam,.ipt,.sldprt,.sldasm" onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || isStreaming}
                  className="p-1.5 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors disabled:opacity-30"
                  title="Upload document"
                  data-testid="upload-btn"
                >
                  {isUploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
                </button>

                {/* Document format menu */}
                <div className="relative">
                  <button
                    onClick={() => { setShowFormatMenu(prev => !prev); setKbPickerOpen(false); setShowModelMenu(false); }}
                    disabled={isStreaming || docGenInThisConvo}
                    className="flex items-center gap-1 p-1.5 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors disabled:opacity-30"
                    title="Generate document"
                    data-testid="format-menu-btn"
                  >
                    {docGenInThisConvo ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
                    <ChevronDown size={10} />
                  </button>

                  <AnimatePresence>
                    {showFormatMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        className="absolute bottom-full left-0 mb-2 w-52 bg-nexus-bg-secondary border border-nexus-border-subtle rounded-xl shadow-2xl overflow-hidden z-50"
                        data-testid="format-dropdown"
                      >
                        <div className="p-1.5">
                          <p className="text-[10px] text-nexus-text-tertiary px-2 py-1 font-medium uppercase tracking-wider">Generate Document</p>
                          {DOC_FORMATS.map((fmt) => (
                            <button
                              key={fmt.id}
                              onClick={() => {
                                setSelectedFormat(fmt);
                                setShowFormatMenu(false);
                              }}
                              className="w-full flex items-center gap-2.5 px-2.5 py-2 text-xs text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 rounded-lg transition-colors"
                              data-testid={`format-${fmt.id}`}
                            >
                              <fmt.icon size={14} style={{ color: fmt.color }} />
                              {fmt.label}
                            </button>
                          ))}
                          {/* Scale override — visible when a scalable format is highlighted */}
                          <div className="mt-1 pt-2 border-t border-nexus-border-subtle px-2 pb-1">
                            <label className="text-[10px] text-nexus-text-tertiary uppercase tracking-wider">Rows / Pages / Slides (optional)</label>
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="e.g. 10000 — let AI decide if blank"
                              value={targetScale}
                              onChange={(e) => setTargetScale(e.target.value.replace(/[^\d]/g, ''))}
                              className="w-full mt-1 px-2 py-1.5 text-xs bg-nexus-bg-tertiary border border-nexus-border-subtle rounded-md text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none focus:border-nexus-accent-mint/40"
                              data-testid="docgen-scale-input"
                            />
                            <p className="text-[9.5px] text-nexus-text-tertiary mt-1 leading-snug">
                              Up to 100,000 rows · 100 pages · 60 slides
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Knowledge-base scope picker — pick one or more KBs to
                    restrict the assistant's retrieval to. Empty = all KBs. */}
                <div className="relative">
                  <button
                    onClick={() => { setKbPickerOpen(prev => !prev); setShowFormatMenu(false); setShowModelMenu(false); }}
                    disabled={isStreaming || docGenInThisConvo}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors text-[11px] disabled:opacity-30 ${
                      selectedKbIds.length
                        ? 'text-nexus-accent-mint bg-nexus-accent-mint/10 hover:bg-nexus-accent-mint/15'
                        : 'text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50'
                    }`}
                    title={selectedKbIds.length
                      ? `Scoped to ${selectedKbIds.length} knowledge base${selectedKbIds.length === 1 ? '' : 's'}`
                      : 'Knowledge bases — All (click to scope)'}
                    data-testid="kb-picker-btn"
                  >
                    <Database size={13} />
                    <span className="hidden sm:inline max-w-[120px] truncate">
                      {selectedKbIds.length === 0
                        ? 'All KBs'
                        : selectedKbIds.length === 1
                          ? (kbList.find(k => k.id === selectedKbIds[0])?.name || '1 KB')
                          : `${selectedKbIds.length} KBs`}
                    </span>
                    <ChevronDown size={10} />
                  </button>

                  <AnimatePresence>
                    {kbPickerOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        className="absolute bottom-full left-0 mb-2 w-64 bg-nexus-bg-secondary border border-nexus-border-subtle rounded-xl shadow-2xl overflow-hidden z-50"
                        data-testid="kb-picker-dropdown"
                      >
                        <div className="px-3 py-2 border-b border-nexus-border-subtle">
                          <p className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">Restrict answers to</p>
                          <p className="text-[11px] text-nexus-text-secondary mt-0.5">
                            Empty = use every KB for this tenant
                          </p>
                        </div>

                        {/* "All KBs" reset row */}
                        <button
                          type="button"
                          onClick={() => { setSelectedKbIds([]); }}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-nexus-bg-tertiary/40 transition-colors ${
                            selectedKbIds.length === 0 ? 'text-nexus-accent-mint' : 'text-nexus-text-secondary'
                          }`}
                          data-testid="kb-picker-all"
                        >
                          <span className="flex items-center gap-2">
                            <Database size={12} />
                            All knowledge bases
                          </span>
                          {selectedKbIds.length === 0 && <Check size={12} />}
                        </button>

                        <div className="max-h-64 overflow-y-auto py-1 border-t border-nexus-border-subtle/50">
                          {kbList.length === 0 ? (
                            <p className="px-3 py-3 text-[11px] text-nexus-text-tertiary text-center">
                              No knowledge bases yet. Create one from the Knowledge page.
                            </p>
                          ) : (
                            kbList.map(kb => {
                              const isSelected = selectedKbIds.includes(kb.id);
                              return (
                                <button
                                  type="button"
                                  key={kb.id}
                                  onClick={() => {
                                    setSelectedKbIds(prev =>
                                      prev.includes(kb.id) ? prev.filter(x => x !== kb.id) : [...prev, kb.id]
                                    );
                                  }}
                                  className={`w-full flex items-start justify-between gap-2 px-3 py-2 text-left hover:bg-nexus-bg-tertiary/40 transition-colors ${
                                    isSelected ? 'bg-nexus-accent-mint/5' : ''
                                  }`}
                                  data-testid={`kb-picker-item-${kb.id}`}
                                >
                                  <span className="min-w-0">
                                    <span className={`block text-xs truncate ${isSelected ? 'text-nexus-accent-mint' : 'text-nexus-text-primary'}`}>
                                      {kb.name}
                                    </span>
                                    <span className="block text-[10px] text-nexus-text-tertiary">
                                      {kb.chunk_count || 0} chunks
                                    </span>
                                  </span>
                                  {isSelected && <Check size={12} className="text-nexus-accent-mint mt-1 flex-shrink-0" />}
                                </button>
                              );
                            })
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Reply language picker — English / Japanese / Arabic.
                    Server appends a `<reply_language>` block to the
                    system prompt so the assistant answers in the
                    selected language; persisted in localStorage. */}
                <div className="relative">
                  <button
                    onClick={() => { setLangPickerOpen(prev => !prev); setShowFormatMenu(false); setKbPickerOpen(false); setShowModelMenu(false); }}
                    disabled={isStreaming || docGenInThisConvo}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors text-[11px] disabled:opacity-30 ${
                      language !== 'en'
                        ? 'text-nexus-accent-mint bg-nexus-accent-mint/10 hover:bg-nexus-accent-mint/15'
                        : 'text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50'
                    }`}
                    title={`Reply language: ${({en:'English', ja:'Japanese', ar:'Arabic'})[language]}`}
                    data-testid="lang-picker-btn"
                  >
                    <Globe size={13} />
                    <span className="hidden sm:inline">
                      {({en:'English', ja:'日本語', ar:'العربية'})[language]}
                    </span>
                    <ChevronDown size={10} />
                  </button>
                  <AnimatePresence>
                    {langPickerOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        className="absolute bottom-full left-0 mb-2 w-52 bg-nexus-bg-secondary border border-nexus-border-subtle rounded-xl shadow-2xl overflow-hidden z-50"
                        data-testid="lang-picker-dropdown"
                      >
                        <div className="px-3 py-2 border-b border-nexus-border-subtle">
                          <p className="text-[10px] uppercase tracking-wider text-nexus-text-tertiary">Reply language</p>
                        </div>
                        {[
                          { id: 'en', label: 'English', native: 'English' },
                          { id: 'ja', label: 'Japanese', native: '日本語' },
                          { id: 'ar', label: 'Arabic', native: 'العربية' },
                        ].map((opt) => (
                          <button
                            type="button"
                            key={opt.id}
                            onClick={() => { setLanguage(opt.id); setLangPickerOpen(false); }}
                            className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-nexus-bg-tertiary/40 transition-colors ${
                              language === opt.id ? 'text-nexus-accent-mint' : 'text-nexus-text-secondary'
                            }`}
                            data-testid={`lang-picker-${opt.id}`}
                          >
                            <span className="flex flex-col">
                              <span className="text-xs">{opt.label}</span>
                              <span className="text-[10px] text-nexus-text-tertiary">{opt.native}</span>
                            </span>
                            {language === opt.id && <Check size={12} />}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Model selector */}
                <div className="relative">
                  <button
                    onClick={() => { setShowModelMenu(prev => !prev); setShowFormatMenu(false); setKbPickerOpen(false); }}
                    className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50 transition-colors text-[11px]"
                    data-testid="model-selector-btn"
                    title={(() => {
                      const m = availableModels.find(x => x.id === selectedModel);
                      return m ? `${m.label} — ${m.multiplier?.toFixed(1) || '1.0'}× credit rate per call` : 'Model';
                    })()}
                  >
                    <Cpu size={13} />
                    <span className="hidden sm:inline max-w-[100px] truncate">
                      {availableModels.find(m => m.id === selectedModel)?.label || 'Model'}
                    </span>
                    {(() => {
                      const m = availableModels.find(x => x.id === selectedModel);
                      const mult = m?.multiplier;
                      if (!mult) return null;
                      return (
                        <span className="hidden md:inline ig-mono text-[9.5px] px-1 rounded"
                              style={{ background: 'rgba(242,107,31,0.10)', color: '#F26B1F' }}>
                          {mult.toFixed(1)}×
                        </span>
                      );
                    })()}
                    <ChevronDown size={10} />
                  </button>

                  <AnimatePresence>
                    {showModelMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: 8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        className="absolute bottom-full left-0 mb-2 w-72 bg-nexus-bg-secondary border border-nexus-border-subtle rounded-xl shadow-2xl overflow-hidden z-50"
                        data-testid="model-dropdown"
                      >
                        <div className="p-1.5">
                          <p className="text-[10px] text-nexus-text-tertiary px-2 py-1 font-medium uppercase tracking-wider">Select Model</p>
                          {availableModels.map(model => (
                              <button
                                key={model.id}
                                onClick={() => { setSelectedModel(model.id); setShowModelMenu(false); }}
                                className={`w-full flex items-center gap-2.5 px-2.5 py-2 text-xs rounded-lg transition-colors ${
                                  selectedModel === model.id
                                    ? 'bg-nexus-accent-mint/10 text-nexus-accent-mint'
                                    : 'text-nexus-text-secondary hover:bg-nexus-bg-tertiary/50'
                                }`}
                                data-testid={`model-${model.id}`}
                              >
                                <Cpu size={13} className={selectedModel === model.id ? 'text-nexus-accent-mint' : 'text-nexus-text-tertiary'} />
                                <div className="text-left flex-1">
                                  <span className="block">{model.label}</span>
                                  <span className="block text-[10px] text-nexus-text-tertiary">{model.desc}</span>
                                </div>
                                {model.multiplier != null && (
                                  <span className="ig-mono text-[10px] px-1.5 py-0.5 rounded font-semibold"
                                        style={{ background: 'rgba(242,107,31,0.10)', color: '#F26B1F' }}
                                        title={`${model.multiplier.toFixed(1)}× credit rate per 1,000 tokens`}>
                                    {model.multiplier.toFixed(1)}×
                                  </span>
                                )}
                                {model.tier === 'premium' && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-400 font-medium">PRO</span>
                                )}
                              </button>
                            ))}
                          <div className="px-2.5 py-2 mt-1 border-t border-nexus-border-subtle text-[10px] text-nexus-text-tertiary leading-relaxed">
                            1 credit = 1,000 base tokens. Cost = ⌈tokens÷1000⌉ × multiplier. {' '}
                            <a href="/portal/pricing#model-cost" className="text-nexus-accent-mint hover:underline" data-testid="model-cost-learn-more">See full pricing →</a>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <textarea
                ref={(el) => { textareaRef.current = el; inputRef.current = el; }}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={selectedFormat ? `Describe what to generate as ${selectedFormat.label}...` : uploadedFile ? `Ask about ${uploadedFile.name}...` : 'Ask anything...'}
                disabled={isStreaming || docGenInThisConvo}
                rows={1}
                className="w-full bg-transparent px-4 py-2 pr-14 text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary resize-none focus:outline-none"
                style={{ minHeight: '40px', maxHeight: '160px' }}
                data-testid="chat-input"
              />
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.92 }}
                onClick={
                  isStreaming ? stopStream :
                  docGenInThisConvo ? (() => stopDocGenJob()) :
                  selectedFormat ? () => handleGenerateDoc(selectedFormat) :
                  handleSend
                }
                disabled={!isStreaming && !docGenInThisConvo && !input.trim()}
                title={isStreaming ? 'Stop generation' : docGenInThisConvo ? 'Stop document generation' : 'Send message'}
                className={`absolute right-2 bottom-2 p-2.5 rounded-xl disabled:opacity-30 disabled:cursor-not-allowed transition-all ${
                  (isStreaming || docGenInThisConvo)
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-nexus-accent-mint text-nexus-bg-primary'
                }`}
                data-testid={(isStreaming || docGenInThisConvo) ? 'stop-message-btn' : 'send-message-btn'}
              >
                {(isStreaming || docGenInThisConvo)
                  ? <span className="block w-3 h-3 bg-white rounded-sm" />
                  : <Send size={16} />}
              </motion.button>
            </div>
            <p className="text-[11px] text-nexus-text-tertiary text-center mt-2 select-none">
              Enter to send &middot; Shift+Enter for new line &middot; Upload docs for context &middot; Generate documents via toolbar
            </p>
          </div>
        </div>
      </div>

      {/* Memory Drawer — stored conversation summary (read-only audit) */}
      <MemoryDrawer
        open={memoryDrawerOpen}
        onClose={() => setMemoryDrawerOpen(false)}
        summary={conversationData?.summary}
        messagesCompressed={memory?.messages_compressed ?? conversationData?.summary_cursor ?? 0}
        recentWindow={memory?.recent_window}
        conversationTitle={conversationData?.title}
        conversationId={conversationId}
        pinnedFacts={conversationData?.pinned_facts || []}
      />

      {/* HTML Artifact Preview drawer — opens when user clicks
          "Open Preview" on an assistant bubble that contains a
          fenced ```html block or a full <html>...</html> document. */}
      <HtmlArtifactPanel
        open={artifactOpen}
        onClose={() => setArtifactOpen(false)}
        html={activeArtifact?.html || ''}
        title={activeArtifact?.title}
        conversationId={activeArtifact?.conversationId}
      />

      {/* DocGen Activity Drawer — per-conversation audit log of every
          docgen job (started / cancelled / completed / failed). */}
      <DocGenActivityDrawer
        open={activityDrawerOpen}
        onClose={() => setActivityDrawerOpen(false)}
        conversationId={conversationId}
      />

      {/* M6 Wow — Insight Reveal particle toast */}
      <InsightRevealToast insight={insight} onDismiss={() => setInsight(null)} />

      {/* APS Viewer + AI extraction drawer — opens when the user clicks
          "Open viewer + AI" on a 3D asset they dropped into the composer. */}
      <ApsViewerPanel
        asset={inlineAsset}
        open={showAssetPanel && !!inlineAsset && (inlineAsset.status || '').toLowerCase() === 'success'}
        onClose={() => setShowAssetPanel(false)}
      />

      {/* Binary-MPP conversion-help modal — pops when a user drops a .mpp
          file. Server-side MPP parsing depends on a Java runtime that's
          not always available in production, so we proactively show the
          File → Save As → XML workflow before the upload even starts. */}
      <MppConversionHelpModal
        open={!!pendingMppFile}
        fileName={pendingMppFile?.name}
        onTryAnyway={async () => {
          const f = pendingMppFile;
          setPendingMppFile(null);
          if (f) await _doFileUpload(f);
        }}
        onCancel={() => {
          setPendingMppFile(null);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }}
      />
    </div>
  );
};

// Sidebar conversation row with inline rename + delete confirmation
const ConversationItem = ({ convo, index, isActive, onSelect, onRenamed, onDeleted }) => {
  const [renaming, setRenaming] = useState(false);
  const [title, setTitle] = useState(convo.title);
  const [showMenu, setShowMenu] = useState(false);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (renaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [renaming]);

  useEffect(() => {
    if (!showMenu) return;
    const onClick = (e) => { if (!menuRef.current?.contains(e.target)) setShowMenu(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showMenu]);

  const commitRename = async () => {
    const next = (title || '').trim();
    if (!next || next === convo.title) { setRenaming(false); setTitle(convo.title); return; }
    try {
      await portalConversationsApi.rename(convo.id, next);
      onRenamed?.();
      toast.success('Renamed');
    } catch { toast.error('Could not rename'); setTitle(convo.title); }
    setRenaming(false);
  };

  const confirmDelete = async () => {
    if (!window.confirm(`Delete "${convo.title}"? This cannot be undone.`)) return;
    try {
      await portalConversationsApi.remove(convo.id);
      onDeleted?.();
      toast.success('Deleted');
    } catch { toast.error('Could not delete'); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.3 }}
      onClick={renaming ? undefined : onSelect}
      className={`group relative p-3 rounded-lg cursor-pointer transition-all duration-200 ${
        isActive
          ? 'bg-nexus-accent-mint/8 border-l-2 border-nexus-accent-mint'
          : 'hover:bg-nexus-bg-tertiary/30 border-l-2 border-transparent'
      }`}
      data-testid={`conversation-item-${convo.id}`}
    >
      <div className="relative">
        {renaming ? (
          <input
            ref={inputRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
              if (e.key === 'Escape') { setTitle(convo.title); setRenaming(false); }
            }}
            className="w-full bg-nexus-bg-tertiary/40 border border-nexus-border-emphasized rounded px-2 py-1 text-sm text-nexus-text-primary outline-none"
            data-testid={`conv-rename-input-${convo.id}`}
            maxLength={120}
          />
        ) : (
          <p className="text-sm text-nexus-text-primary truncate pr-7">{convo.title}</p>
        )}
        <p className="text-[11px] text-nexus-text-tertiary mt-0.5">
          {new Date(convo.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
        </p>
        {!renaming && (
          <div ref={menuRef}
            className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowMenu((s) => !s)}
              className="p-1 rounded text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-nexus-bg-tertiary/40"
              aria-label="Conversation actions"
              data-testid={`conversation-kebab-${convo.id}`}>
              <MoreVertical size={14} />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-36 rounded-lg shadow-2xl z-50 overflow-hidden text-sm"
                style={{ background: 'var(--layer-1)', border: '1px solid var(--hairline)' }}>
                <button onClick={() => { setShowMenu(false); setRenaming(true); }}
                  className="w-full text-left px-3 py-2 hover:bg-nexus-bg-tertiary/40 text-nexus-text-primary text-[12px] flex items-center gap-2"
                  data-testid={`conversation-menu-rename-${convo.id}`}>
                  <Pencil size={12} /> Rename
                </button>
                <button onClick={() => { setShowMenu(false); confirmDelete(); }}
                  className="w-full text-left px-3 py-2 hover:bg-red-500/10 text-red-400 text-[12px] flex items-center gap-2"
                  data-testid={`conversation-menu-delete-${convo.id}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Memory Drawer — shows the Claude-generated running summary for audit/transparency
const MemoryDrawer = ({ open, onClose, summary, messagesCompressed, recentWindow, conversationTitle, conversationId, pinnedFacts }) => {
  const [copied, setCopied] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [isPinning, setIsPinning] = useState(false);
  const queryClient = useQueryClient();

  const handleCopy = async () => {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Copy failed');
    }
  };

  const handlePin = async () => {
    const text = pinInput.trim();
    if (!text || !conversationId) return;
    setIsPinning(true);
    try {
      await portalConversationsApi.pinFact(conversationId, text);
      setPinInput('');
      toast.success('Fact pinned — it will be respected in every future reply');
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to pin');
    } finally {
      setIsPinning(false);
    }
  };

  const handleUnpin = async (pinId) => {
    try {
      await portalConversationsApi.unpinFact(conversationId, pinId);
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    } catch {
      toast.error('Failed to remove pin');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            data-testid="memory-drawer-backdrop"
          />
          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[480px] z-50 bg-nexus-surface border-l border-nexus-border-subtle shadow-2xl flex flex-col"
            data-testid="memory-drawer"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-nexus-border-subtle">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-nexus-accent-mint/10 border border-nexus-accent-mint/20 flex items-center justify-center flex-shrink-0">
                    <Brain size={16} className="text-nexus-accent-mint" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-heading text-sm font-semibold text-nexus-text-primary">Conversation Memory</h2>
                    <p className="text-[11px] text-nexus-text-tertiary truncate">{conversationTitle || 'Current conversation'}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-nexus-text-tertiary hover:text-nexus-text-primary hover:bg-nexus-bg-tertiary/50 transition-colors flex-shrink-0"
                data-testid="memory-drawer-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Meta row */}
            <div className="px-5 py-3 border-b border-nexus-border-subtle flex flex-wrap items-center gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-nexus-accent-mint/10 text-nexus-accent-mint border border-nexus-accent-mint/20 font-medium">
                {messagesCompressed} compressed
              </span>
              {recentWindow ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-nexus-bg-tertiary/50 text-nexus-text-secondary border border-nexus-border-subtle">
                  Last {recentWindow} sent verbatim
                </span>
              ) : null}
              {pinnedFacts?.length > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-nexus-accent-violet/10 text-nexus-accent-violet border border-nexus-accent-violet/20 font-medium">
                  <Pin size={10} /> {pinnedFacts.length} pinned
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-nexus-bg-tertiary/50 text-nexus-text-secondary border border-nexus-border-subtle">
                Summarized by Cognara Engine · Pro 4.6
              </span>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* Pinned Facts section */}
              <section data-testid="pinned-facts-section">
                <div className="flex items-center gap-2 mb-2">
                  <Pin size={13} className="text-nexus-accent-violet" />
                  <h3 className="text-xs font-semibold text-nexus-text-primary uppercase tracking-wider">Pinned Facts</h3>
                </div>
                <p className="text-[11px] text-nexus-text-tertiary leading-relaxed mb-3">
                  Pinned facts are injected verbatim into every reply and never age out or get summarized.
                  Perfect for contract clauses, safety rules, or regulatory constraints.
                </p>

                {pinnedFacts && pinnedFacts.length > 0 ? (
                  <ul className="space-y-2 mb-3">
                    {pinnedFacts.map((p) => (
                      <li
                        key={p.id}
                        className="group flex items-start gap-2 px-3 py-2 rounded-lg bg-nexus-accent-violet/5 border border-nexus-accent-violet/20 text-[12.5px] text-nexus-text-secondary leading-relaxed"
                        data-testid={`pin-${p.id}`}
                      >
                        <Pin size={11} className="text-nexus-accent-violet mt-1 flex-shrink-0" />
                        <span className="flex-1 whitespace-pre-wrap break-words">{p.text}</span>
                        <button
                          onClick={() => handleUnpin(p.id)}
                          className="p-1 rounded text-nexus-text-tertiary hover:text-nexus-error hover:bg-nexus-error/10 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                          title="Remove pin"
                          data-testid={`unpin-${p.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[11px] text-nexus-text-tertiary italic mb-3">No pins yet.</p>
                )}

                {/* Add pin form */}
                <div className="flex flex-col gap-2">
                  <textarea
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    placeholder="e.g., Budget ceiling is ₹82 crore INR. Must not exceed under any circumstance."
                    rows={2}
                    maxLength={2000}
                    className="w-full px-3 py-2 rounded-lg bg-nexus-input border border-nexus-border-subtle text-[12.5px] text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none focus:border-nexus-accent-mint resize-none"
                    data-testid="pin-input"
                  />
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-nexus-text-tertiary">{pinInput.length}/2000</span>
                    <button
                      onClick={handlePin}
                      disabled={!pinInput.trim() || isPinning || !conversationId}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-nexus-accent-mint text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                      data-testid="pin-fact-btn"
                    >
                      <Pin size={12} />
                      {isPinning ? 'Pinning…' : 'Pin a fact'}
                    </button>
                  </div>
                </div>
              </section>

              {/* Divider */}
              <div className="h-px bg-nexus-border-subtle" />

              {/* Summary section */}
              <section>
                <div className="flex items-center gap-2 mb-2">
                  <Brain size={13} className="text-nexus-accent-mint" />
                  <h3 className="text-xs font-semibold text-nexus-text-primary uppercase tracking-wider">Running Summary</h3>
                </div>
                {summary ? (
                  <>
                    <p className="text-[11px] text-nexus-text-tertiary leading-relaxed mb-3">
                      Read-only summary the assistant references when answering about earlier parts of this thread.
                      Regenerated automatically as the conversation grows.
                    </p>
                    <pre
                      className="whitespace-pre-wrap text-[13px] leading-relaxed text-nexus-text-secondary font-body bg-nexus-bg-tertiary/40 border border-nexus-border-subtle rounded-xl p-4"
                      data-testid="memory-drawer-summary"
                    >
                      {summary}
                    </pre>
                  </>
                ) : (
                  <p className="text-[12px] text-nexus-text-tertiary italic">
                    No summary yet — generated once a conversation exceeds 60K tokens.
                  </p>
                )}
              </section>
            </div>

            {/* Footer actions */}
            {summary && (
              <div className="px-5 py-3 border-t border-nexus-border-subtle flex items-center justify-between">
                <p className="text-[11px] text-nexus-text-tertiary">
                  Summary: {summary.length.toLocaleString()} chars
                </p>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-medium bg-nexus-accent-mint text-white hover:opacity-90 transition-opacity"
                  data-testid="memory-drawer-copy"
                >
                  {copied ? 'Copied' : 'Copy summary'}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

// Empty state with quick prompts
const EmptyChat = ({ onPromptClick }) => {
  const prompts = [
    { text: 'What are EPC best practices?', icon: Sparkles },
    { text: 'Help with procurement strategy', icon: Sparkles },
    { text: 'Cost estimation guidelines', icon: Sparkles },
    { text: 'Risk assessment framework', icon: Sparkles },
  ];

  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center max-w-lg">
        {/* M6 Wow — Agent Orchestration graph as hero visual */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex justify-center mb-1"
          aria-hidden="true"
        >
          <AgentOrchestrationGraph active activeAgent="sutradhar" size="compact" />
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="font-heading text-xl font-semibold text-nexus-text-primary"
        >
          How can I help you today?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="text-nexus-text-secondary mt-2 text-sm"
        >
          Ask about EPC projects, procurement, engineering, or capital management.
        </motion.p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {prompts.map((p, i) => (
            <motion.button
              key={p.text}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 + i * 0.06 }}
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => onPromptClick(p.text)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-left text-nexus-text-secondary bg-nexus-bg-tertiary/30 border border-nexus-border-subtle rounded-xl hover:bg-nexus-bg-tertiary/50 hover:border-nexus-border-emphasized transition-colors"
              data-testid={`prompt-${i}`}
            >
              <p.icon size={14} className="text-nexus-accent-mint flex-shrink-0" />
              {p.text}
            </motion.button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PortalChat;
